import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  HelpCircle,
  ChevronDown,
  Search,
  Sparkles,
  Building2,
  UserCircle,
  ShieldCheck,
  LifeBuoy,
  BriefcaseBusiness,
  Smartphone,
  BadgeDollarSign,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import Footer from '@/components/pages/shared/Footer';
import FaqSEO from '@/components/seo/FAQSEO';
import { Input } from '@/components/ui/input';
import {
  cargarPromocionActiva,
  formatearDescripcionPromocion,
  type PromocionActiva,
} from '@/components/hooks/usePromocion';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FAQItem {
  pregunta: string;
  respuesta: React.ReactNode;
  keywords?: string[];
}

interface FAQCategory {
  categoria: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────
// buildFaqData recibe el texto de la promo activa (ya resuelto desde la BD,
// con la fecha/duración real) para no tener ninguna fecha hardcodeada aquí.
// respuestaGratis: texto ya formateado (o null si no hay promo activa ahora).
function buildFaqData(respuestaGratis: string | null): FAQCategory[] {
  return [
    {
      categoria: 'General',
      icon: <Sparkles className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Qué es Bookoll?',
          keywords: [
            'plataforma',
            'app',
            'aplicacion',
            'herramienta',
            'software',
            'sistema',
            'reservas',
            'citas',
          ],
          respuesta: (
            <div className="space-y-3">
              <p>
                Bookoll es una plataforma digital diseñada para ayudar a
                profesionales, autónomos y pequeñas empresas a gestionar su
                negocio y sus reservas online de forma sencilla. Permite organizar
                agendas, administrar citas y facilitar que los clientes puedan
                reservar servicios de manera rápida y cómoda.
              </p>
              <p>
                Además de funcionar como sistema de reservas, Bookoll ofrece un
                perfil profesional online donde cada negocio puede mostrar
                información sobre su actividad, publicar los servicios que ofrece,
                añadir productos y compartir una galería de imágenes para que los
                clientes conozcan mejor su trabajo.
              </p>
              <p>
                De esta forma, Bookoll no solo ayuda a gestionar citas, sino que
                también actúa como un escaparate digital para el negocio,
                facilitando que los clientes descubran los servicios, reserven
                fácilmente y mantengan una relación más directa con el
                profesional.
              </p>
            </div>
          ),
        },
        {
          pregunta: '¿Para quién está pensado Bookoll?',
          keywords: [
            'autonomo',
            'pyme',
            'negocio',
            'empresa',
            'profesional',
            'quien puede usar',
          ],
          respuesta: (
            <p>
              Bookoll está pensado para profesionales, autónomos y pymes que
              trabajan con citas previas. Además de gestionar tu agenda, Bookoll
              te proporciona un perfil profesional online que actúa como un
              escaparate digital para tu negocio. En él puedes mostrar información
              sobre tu actividad, publicar tu catálogo de servicios, añadir
              productos y facilitar que los clientes te descubran y reserven
              fácilmente.
            </p>
          ),
        },
        {
          pregunta: '¿Qué tipos de usuario existen en Bookoll?',
          keywords: [
            'perfil',
            'roles',
            'tipos de cuenta',
            'empresa',
            'trabajador',
            'cliente',
          ],
          respuesta: (
            <ul className="list-disc list-inside space-y-1.5">
              <li>
                <strong>Empresa:</strong> administra el perfil profesional, el
                negocio, los servicios, los trabajadores y las citas.
              </li>
              <li>
                <strong>Trabajador:</strong> consulta su agenda, gestiona sus
                citas y visualiza su panel personalizado.
              </li>
              <li>
                <strong>Cliente:</strong> reserva citas, consulta su historial y
                gestiona sus datos desde su panel.
              </li>
            </ul>
          ),
        },
        {
          pregunta: '¿Qué tipo de profesionales pueden usar Bookoll?',
          keywords: [
            'fisioterapeuta',
            'psicologo',
            'esteticista',
            'nutricionista',
            'entrenador',
            'terapeuta',
            'salon',
            'peluqueria',
            'medico',
            'sectores',
          ],
          respuesta: (
            <div className="space-y-3">
              <p>
                Bookoll puede ser útil para muchos profesionales que trabajan con
                citas previas. Entre otros:
              </p>
              <ul className="list-disc list-inside space-y-1 columns-2">
                <li>Fisioterapeutas</li>
                <li>Psicólogos</li>
                <li>Entrenadores personales</li>
                <li>Esteticistas</li>
                <li>Nutricionistas</li>
                <li>Terapeutas</li>
                <li>Profesionales de salud o bienestar</li>
              </ul>
              <p>
                Pero no se limita solo a estos sectores. Si crees que Bookoll
                puede ayudarte a organizar tu agenda, gestionar tu negocio o
                facilitar las reservas de tus clientes, puedes probar la
                plataforma y ver si se adapta a tu forma de trabajar.
              </p>
            </div>
          ),
        },
        {
          pregunta: '¿Cuánto se tarda en empezar a usar Bookoll?',
          keywords: [
            'configurar',
            'inicio',
            'empezar',
            'comenzar',
            'setup',
            'alta',
            'rapido',
          ],
          respuesta: (
            <p>
              Muy poco. En solo unos minutos puedes crear tu cuenta, configurar
              tus servicios y definir tu disponibilidad. Una vez hecho esto, ya
              tendrás tu agenda lista para empezar a recibir reservas y tu perfil
              profesional visible para los clientes.
            </p>
          ),
        },
      ],
    },
    {
      categoria: 'Para Empresas',
      icon: <Building2 className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Cómo registro mi negocio?',
          keywords: [
            'crear cuenta',
            'alta empresa',
            'registrar negocio',
            'nuevo negocio',
            'como empezar',
            'pasos registro',
          ],
          respuesta: (
            <div className="space-y-3">
              <p>
                Registrar tu negocio en Bookoll es rápido y sencillo. El proceso
                tarda menos de 5 minutos y no necesitas tarjeta de crédito.
              </p>
              <div className="space-y-2">
                <p>
                  <strong>Paso 1 — Crear tu cuenta</strong>
                  <br />
                  Introduce tu correo electrónico y crea una contraseña segura.
                  También puedes registrarte usando tu cuenta de Google.
                </p>
                <p>
                  <strong>Paso 2 — Información básica del negocio</strong>
                  <br />
                  Añade el logo (opcional), nombre, categoría y una breve
                  descripción de tus servicios.
                </p>
                <p>
                  <strong>Paso 3 — Ubicación y contacto</strong>
                  <br />
                  Indica país, ciudad, localidad, dirección y teléfono de
                  contacto.
                </p>
                <p>
                  <strong>Paso 4 — Confirmación</strong>
                  <br />
                  Acepta las condiciones de uso y la política de privacidad para
                  finalizar el registro.
                </p>
              </div>
              <p>
                Una vez completado, tendrás acceso inmediato a tu panel de empresa
                donde podrás configurar tu agenda, añadir servicios, trabajadores
                y empezar a aceptar reservas online.
              </p>
            </div>
          ),
        },
        {
          pregunta: '¿Qué información puedo configurar en el perfil de empresa?',
          keywords: [
            'editar perfil',
            'datos empresa',
            'logo',
            'descripcion',
            'horario',
            'redes sociales',
            'galeria',
          ],
          respuesta: (
            <ul className="list-disc list-inside space-y-1">
              <li>Nombre, categoría, logo y descripción.</li>
              <li>Dirección y ciudad.</li>
              <li>Teléfono, email y redes sociales.</li>
              <li>Horario de apertura por día de la semana.</li>
              <li>Días festivos del negocio.</li>
              <li>Galería de fotos y catálogo de productos.</li>
            </ul>
          ),
        },
        {
          pregunta: '¿Cómo se configuran los servicios?',
          keywords: [
            'crear servicio',
            'añadir servicio',
            'precio servicio',
            'duracion',
            'capacidad',
            'disponibilidad servicio',
          ],
          respuesta: (
            <p>
              Cada empresa puede crear y configurar sus propios servicios
              indicando el nombre, precio, duración en minutos, capacidad máxima
              de clientes y los días de la semana en los que el servicio está
              disponible. Además, cada servicio puede requerir un trabajador
              asignado o no, permitir reservas en grupo y tener horarios
              específicos independientes del horario general del negocio.
            </p>
          ),
        },
        {
          pregunta:
            '¿Puedo configurar que un servicio solo esté disponible ciertos días?',
          keywords: [
            'dias servicio',
            'horario servicio',
            'limitar dias',
            'disponibilidad dias',
            'lunes martes',
          ],
          respuesta: (
            <p>
              Sí. Cada servicio permite asociar los días de la semana en que se
              ofrece (lunes a domingo de forma independiente). Si un día no está
              marcado, no aparecerá como disponible en el calendario de reservas.
            </p>
          ),
        },
        {
          pregunta: '¿Cómo gestiono a los trabajadores?',
          keywords: [
            'añadir trabajador',
            'invitar trabajador',
            'codigo invitacion',
            'asignar servicios',
            'horario trabajador',
            'equipo',
          ],
          respuesta: (
            <p>
              Desde <strong>Panel Empresa → Trabajadores</strong> puedes invitar
              usuarios con perfil trabajador mediante un código de invitación,
              asignarles servicios específicos y configurar su horario semanal de
              forma independiente al horario general del negocio.
            </p>
          ),
        },
        {
          pregunta: '¿Qué son las ausencias?',
          keywords: [
            'vacaciones',
            'baja',
            'permiso',
            'enfermedad',
            'dias libres',
            'ausencia trabajador',
          ],
          respuesta: (
            <p>
              El módulo de ausencias permite registrar vacaciones, permisos o
              bajas de los trabajadores con fecha de inicio y fin. Cada ausencia
              tiene un tipo (vacaciones, permiso, enfermedad…) y un estado
              (pendiente, aprobado, rechazado) que el gestor puede actualizar.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo activar o desactivar las reservas online?',
          keywords: [
            'parar reservas',
            'cerrar reservas',
            'bloquear reservas',
            'deshabilitar reservas',
            'apagar reservas',
          ],
          respuesta: (
            <p>
              Sí. Desde la configuración de empresa puedes activar o desactivar
              las reservas online de citas en cualquier momento. También puedes
              restringir las reservas para que solo las realicen clientes
              registrados en Bookoll.
            </p>
          ),
        },
        {
          pregunta:
            '¿Cuántos días de antelación se muestran en el calendario de reservas?',
          keywords: [
            'dias disponibles',
            'calendario reservas',
            'antelacion',
            'ventana reservas',
            'cuantos dias',
          ],
          respuesta: (
            <p>
              Es configurable por empresa, entre 1 y 60 días. El valor por defecto
              es <strong>14 días</strong>. Los clientes solo verán los días con
              disponibilidad real según el horario y las citas ya existentes.
            </p>
          ),
        },
        {
          pregunta: '¿Qué son los bonos?',
          keywords: [
            'pack sesiones',
            'bono sesiones',
            'paquete',
            'prepago',
            'voucher',
            'sesiones prepagadas',
          ],
          respuesta: (
            <p>
              Los bonos son paquetes de sesiones de un servicio concreto que se
              venden a un precio cerrado. Cada bono tiene una cantidad de usos, un
              precio total y una vigencia en días. Los clientes pueden comprar
              bonos y consumirlos en futuras citas.
            </p>
          ),
        },
        {
          pregunta: '¿Qué es la lista negra de clientes?',
          keywords: [
            'bloquear cliente',
            'vetar cliente',
            'prohibir cliente',
            'banear',
            'cliente problematico',
            'no show',
          ],
          respuesta: (
            <p>
              La lista negra permite a los negocios bloquear a clientes que han
              tenido comportamientos problemáticos, como no presentarse a citas,
              cancelar repetidamente en el último momento o incumplir las normas
              del negocio. Cuando un cliente se añade a la lista negra, no podrá
              realizar nuevas reservas en ese negocio a través de Bookoll.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo añadir clientes manualmente?',
          keywords: [
            'agregar cliente',
            'crear cliente',
            'importar clientes',
            'vincular cliente',
            'nuevo cliente',
          ],
          respuesta: (
            <p>
              Sí. Desde <strong>Panel Empresa → Clientes</strong> puedes vincular
              usuarios registrados en Bookoll como clientes de tu empresa, o
              sugerir clientes basándote en las reservas recientes. También existe
              una lista negra para bloquear accesos por teléfono o email.
            </p>
          ),
        },
        {
          pregunta: '¿Qué notificaciones se envían automáticamente?',
          keywords: [
            'email automatico',
            'aviso cita',
            'recordatorio',
            'confirmacion reserva',
            'whatsapp',
            'sms',
            'notificacion',
          ],
          respuesta: (
            <ul className="list-disc list-inside space-y-1">
              <li>Confirmación de cita al cliente, trabajador y gestor.</li>
              <li>Recordatorio configurable (1–72 horas antes) por email.</li>
              <li>Notificación de cancelación o reprogramación.</li>
              <li>
                Cada canal se puede activar o desactivar de forma independiente.
              </li>
            </ul>
          ),
        },
      ],
    },
    {
      categoria: 'Para Trabajadores',
      icon: <BriefcaseBusiness className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Cómo accedo al panel de trabajador?',
          keywords: [
            'login trabajador',
            'entrar panel',
            'acceso trabajador',
            'iniciar sesion trabajador',
          ],
          respuesta: (
            <p>
              Una vez que la empresa te envía el código de invitación y completas
              el registro, accedes desde la pantalla de perfiles a tu panel. Desde
              ahí puedes ver tu agenda, citas del día, citas pendientes y tu
              perfil personal.
            </p>
          ),
        },
        {
          pregunta:
            '¿Puedo ver solo mis citas o también las del resto del equipo?',
          keywords: [
            'ver citas equipo',
            'citas compañeros',
            'visibilidad citas',
            'agenda equipo',
          ],
          respuesta: (
            <p>
              El panel de trabajador muestra únicamente tus citas asignadas. El
              gestor de la empresa tiene visibilidad completa de todas las citas
              de todos los trabajadores.
            </p>
          ),
        },
        {
          pregunta: '¿Cómo configuro mi horario semanal?',
          keywords: [
            'cambiar horario',
            'editar horario',
            'horario semana',
            'disponibilidad trabajador',
            'franjas horarias',
          ],
          respuesta: (
            <p>
              Desde <strong>Panel Trabajador → Mi perfil</strong> puedes ver tu
              horario asignado. El gestor de la empresa es quien configura los
              horarios de cada trabajador por día de la semana y franjas horarias.
              Si necesitas un cambio, contacta con el gestor.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo solicitar ausencias desde mi panel?',
          keywords: [
            'pedir vacaciones',
            'solicitar permiso',
            'pedir baja',
            'dias libres trabajador',
          ],
          respuesta: (
            <p>
              Sí. Desde el panel de trabajador puedes registrar solicitudes de
              ausencia indicando el tipo, las fechas y un motivo opcional. El
              gestor recibirá la solicitud y podrá aprobarla o rechazarla.
            </p>
          ),
        },
        {
          pregunta: '¿Para qué sirve el código QR del trabajador?',
          keywords: [
            'qr trabajador',
            'codigo qr',
            'escanear qr',
            'compartir reservas',
            'link reservas',
          ],
          respuesta: (
            <p>
              Cada trabajador puede generar y mostrar un QR desde{' '}
              <strong>Panel Trabajador → Mostrar QR</strong>. Al escanearlo, el
              cliente accede directamente a la página de reservas de la empresa,
              facilitando la captación en punto de venta físico.
            </p>
          ),
        },
      ],
    },
    {
      categoria: 'Para Clientes',
      icon: <UserCircle className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Necesito una cuenta para reservar?',
          keywords: [
            'reservar sin registro',
            'reservar sin cuenta',
            'anonimo',
            'sin login',
          ],
          respuesta: (
            <p>
              Depende de la configuración del negocio. Algunos permiten reservar
              sin registro introduciendo nombre, email y teléfono. Otros requieren
              una cuenta activa en Bookoll. En cualquier caso, registrarse es
              gratuito y permite cancelar citas, ver el historial y gestionar el
              perfil.
            </p>
          ),
        },
        {
          pregunta: '¿Cómo realizo una reserva?',
          keywords: [
            'hacer reserva',
            'pedir cita',
            'reservar cita',
            'como reservar',
            'pasos reserva',
          ],
          respuesta: (
            <ol className="list-decimal list-inside space-y-1">
              <li>Accede a la página pública de la empresa.</li>
              <li>Selecciona el servicio.</li>
              <li>Elige profesional (si el servicio lo requiere).</li>
              <li>Selecciona fecha y hora disponible.</li>
              <li>Introduce tus datos de contacto y confirma.</li>
            </ol>
          ),
        },
        {
          pregunta: '¿Puedo reservar para más de una persona?',
          keywords: [
            'reserva grupo',
            'varias personas',
            'multiple personas',
            'reserva colectiva',
            'capacidad',
          ],
          respuesta: (
            <p>
              Sí, siempre que el servicio tenga configurada una capacidad máxima
              mayor que 1. En ese caso, podrás indicar el número de participantes
              al realizar la reserva. El precio se calculará automáticamente según
              la política del servicio (por persona o por servicio).
            </p>
          ),
        },
        {
          pregunta: '¿Cómo cancelo una cita?',
          keywords: [
            'anular cita',
            'eliminar reserva',
            'borrar cita',
            'deshacer reserva',
            'cancelar reserva',
          ],
          respuesta: (
            <p>
              Si tienes cuenta en Bookoll, accede a{' '}
              <strong>Panel Cliente → Mis citas</strong> y cancela la cita
              deseada. También puedes cancelarla desde el email de confirmación.
              Si reservaste sin cuenta, contacta directamente con el negocio para
              solicitar la cancelación.
            </p>
          ),
        },
        {
          pregunta: '¿Recibiré confirmación de mi reserva?',
          keywords: [
            'email confirmacion',
            'aviso reserva',
            'comprobante cita',
            'notificacion reserva',
          ],
          respuesta: (
            <p>
              Sí. Tras confirmar, recibirás un email con los detalles: empresa,
              servicio, profesional, fecha y hora. Además, si el negocio tiene
              activados los recordatorios, recibirás un aviso antes de la cita
              (entre 1 y 72 horas antes, según configuración). Te llegará un aviso
              por email o, si usas la app, como notificación en tu móvil.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo valorar una cita completada?',
          keywords: [
            'resena',
            'opinion',
            'puntuacion',
            'estrella',
            'valoracion',
            'comentario',
            'review',
          ],
          respuesta: (
            <p>
              Sí. Después de que una cita quede marcada como completada, puedes
              dejar una puntuación (de 0 a 5) y un comentario opcional. Las
              valoraciones son visibles en el perfil público de la empresa.
            </p>
          ),
        },
        {
          pregunta: '¿Cómo funciona el panel de cliente?',
          keywords: [
            'dashboard cliente',
            'que puedo hacer',
            'opciones cliente',
            'panel personal',
          ],
          respuesta: (
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Inicio:</strong> resumen de citas del día, próximas citas
                y gasto histórico.
              </li>
              <li>
                <strong>Mis citas:</strong> historial completo con estados y
                opción de cancelar.
              </li>
              <li>
                <strong>Mis bonos:</strong> bonos comprados, usos restantes y
                fecha de vencimiento.
              </li>
              <li>
                <strong>Explorar:</strong> directorio de empresas disponibles en
                Bookoll.
              </li>
              <li>
                <strong>Mi perfil:</strong> gestión de datos personales.
              </li>
            </ul>
          ),
        },
      ],
    },
    {
      categoria: 'Acceso y dispositivos',
      icon: <Smartphone className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Puedo usar Bookoll desde el móvil?',
          keywords: [
            'movil',
            'smartphone',
            'iphone',
            'android',
            'tablet',
            'responsive',
          ],
          respuesta: <p>Sí, funciona desde móvil, tablet y ordenador.</p>,
        },
        {
          pregunta: '¿Tengo que instalar alguna aplicación?',
          keywords: [
            'descargar app',
            'instalar',
            'app store',
            'google play',
            'apk',
          ],
          respuesta: <p>No. Puedes usarlo directamente desde el navegador.</p>,
        },
        {
          pregunta: '¿Puedo usarlo desde cualquier lugar?',
          keywords: [
            'remoto',
            'desde casa',
            'fuera oficina',
            'viaje',
            'internet',
          ],
          respuesta: <p>Sí, siempre que tengas conexión a internet.</p>,
        },
      ],
    },
    {
      categoria: 'Privacidad y Datos',
      icon: <ShieldCheck className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Qué datos personales se recogen?',
          keywords: [
            'datos recogidos',
            'informacion personal',
            'que guardais',
            'datos almacenados',
            'rgpd',
          ],
          respuesta: (
            <ul className="list-disc list-inside space-y-1">
              <li>
                Nombre completo, email y teléfono para la gestión de citas y
                cuenta.
              </li>
              <li>
                Consentimientos registrados con versión de política y fecha de
                aceptación.
              </li>
              <li>
                Historial de reservas y uso de bonos asociados al email del
                usuario.
              </li>
            </ul>
          ),
        },
        {
          pregunta: '¿Cómo se gestionan los consentimientos?',
          keywords: [
            'aceptar terminos',
            'politica privacidad',
            'consentimiento rgpd',
            'revocar consentimiento',
          ],
          respuesta: (
            <p>
              Bookoll registra tres tipos de consentimiento: del propietario de la
              empresa (al registrar el negocio), del usuario cliente (al
              registrarse en la plataforma) y del usuario al realizar una reserva
              sin cuenta. Cada consentimiento queda vinculado a una versión de la
              política de privacidad y puede ser revocado.
            </p>
          ),
        },
        {
          pregunta: '¿Los datos de clientes son compartidos entre empresas?',
          keywords: [
            'compartir datos',
            'vender datos',
            'privacidad datos',
            'acceso datos',
            'datos terceros',
          ],
          respuesta: (
            <p>
              No. Cada empresa solo tiene acceso a los datos de los clientes que
              han reservado en ella. Los datos no se comparten ni son visibles
              para otras empresas de la plataforma.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo solicitar la eliminación de mis datos?',
          keywords: [
            'borrar cuenta',
            'eliminar cuenta',
            'suprimir datos',
            'derecho al olvido',
            'baja',
            'derecho supresion',
          ],
          respuesta: (
            <p>
              Sí. Puedes solicitar la eliminación de tu cuenta y todos los datos
              asociados enviando un email a{' '}
              <a
                href="mailto:privacidad@bookoll.com"
                className="text-[#103254] dark:text-white underline font-medium"
              >
                privacidad@bookoll.com
              </a>
              . Procesaremos la solicitud en un plazo máximo de 30 días conforme
              al RGPD.
            </p>
          ),
        },
        {
          pregunta: '¿Dónde puedo consultar la política de privacidad completa?',
          keywords: [
            'leer privacidad',
            'ver politica',
            'terminos privacidad',
            'aviso legal',
          ],
          respuesta: (
            <p>
              Disponible en{' '}
              <a
                href="/privacy-policy"
                className="text-[#103254] dark:text-white underline font-medium"
              >
                bookoll.com/privacy-policy
              </a>
              . Incluye información sobre responsable del tratamiento,
              finalidades, derechos de los usuarios y plazo de conservación de
              datos.
            </p>
          ),
        },
      ],
    },
    {
      categoria: 'Soporte',
      icon: <LifeBuoy className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Cómo contacto con el equipo de soporte?',
          keywords: [
            'ayuda',
            'contactar',
            'escribir',
            'email soporte',
            'asistencia',
            'atencion cliente',
          ],
          respuesta: (
            <p>
              Escríbenos a{' '}
              <a
                href="mailto:soporte@bookoll.com"
                className="text-[#103254] dark:text-white underline font-medium"
              >
                soporte@bookoll.com
              </a>
              . Respondemos en menos de 24 horas en días laborables.
            </p>
          ),
        },
        {
          pregunta: '¿Qué hago si tengo un problema técnico con una reserva?',
          keywords: [
            'error reserva',
            'fallo reserva',
            'problema cita',
            'no funciona reserva',
            'bug',
          ],
          respuesta: (
            <p>
              Contacta con soporte indicando el email con el que realizaste la
              reserva y la fecha aproximada. Con esos datos podemos localizar la
              cita en el sistema y resolver el incidente.
            </p>
          ),
        },
        {
          pregunta: '¿Cómo reporto un problema con mi cuenta o acceso?',
          keywords: [
            'no puedo entrar',
            'no accedo',
            'cuenta bloqueada',
            'contrasena olvidada',
            'recuperar acceso',
            'problema login',
          ],
          respuesta: (
            <p>
              Si no puedes acceder a tu cuenta, usa la opción{' '}
              <strong>"¿Has olvidado tu contraseña?"</strong> en la pantalla de
              inicio de sesión. Si el problema persiste, escríbenos a{' '}
              <a
                href="mailto:soporte@bookoll.com"
                className="text-[#103254] dark:text-white underline font-medium"
              >
                soporte@bookoll.com
              </a>{' '}
              indicando el email asociado a tu cuenta.
            </p>
          ),
        },
      ],
    },
    {
      categoria: 'Planes y precios',
      icon: <BadgeDollarSign className="w-4 h-4" />,
      items: [
        {
          pregunta: '¿Es gratuito usar Bookoll?',
          keywords: [
            'precio',
            'coste',
            'gratis',
            'free',
            'cuanto cuesta',
            'pago',
            'tarifa',
          ],
          respuesta: (
            <p>
              {respuestaGratis ??
                'Bookoll siempre tiene un plan gratuito disponible para empezar. Consulta los planes de pago en la página de precios.'}
            </p>
          ),
        },
        {
          pregunta: '¿Qué incluyen los planes de pago?',
          keywords: [
            'funciones pago',
            'que trae el plan',
            'caracteristicas plan',
            'diferencia planes',
          ],
          respuesta: (
            <p>
              Los planes pueden incluir diferentes funciones según las necesidades
              del profesional.
            </p>
          ),
        },
        {
          pregunta: '¿Puedo cambiar de plan?',
          keywords: [
            'actualizar plan',
            'subir plan',
            'bajar plan',
            'modificar suscripcion',
            'cambiar tarifa',
          ],
          respuesta: <p>Sí, puedes cambiar tu plan cuando quieras.</p>,
        },
        {
          pregunta: '¿Puedo cancelar mi suscripción?',
          keywords: [
            'desactivar',
            'baja',
            'dar de baja',
            'dejar de pagar',
            'eliminar plan',
            'cancelar plan',
            'dejar bookoll',
          ],
          respuesta: <p>Sí, puedes cancelar cuando lo necesites.</p>,
        },
      ],
    },
  ];
}

// ─── Accordion Item ───────────────────────────────────────────────────────────
const AccordionItem = ({
  item,
  index,
  globalIndex,
}: {
  item: FAQItem;
  index: number;
  globalIndex: number;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.04 }}
      className="group relative border-b border-gray-100 dark:border-white/10 last:border-0"
    >
      <h3 className="m-0 p-0 font-inherit text-inherit">
        <button
          onClick={() => setOpen((p) => !p)}
          aria-expanded={open}
          className="w-full flex items-start gap-5 py-8 text-left"
        >
          <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-[#103254]/6 dark:bg-white/10 flex items-center justify-center text-xs font-bold text-[#103254]/40 dark:text-white/40">
            {String(globalIndex + 1).padStart(2, '0')}
          </span>
          <span className="flex-1 text-base font-semibold text-gray-800 dark:text-gray-100 group-hover:text-[#103254] dark:group-hover:text-white transition-colors leading-snug">
            {item.pregunta}
          </span>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            className="shrink-0 mt-0.5"
          >
            <ChevronDown
              className={`w-5 h-5 transition-colors ${
                open
                  ? 'text-[#288E7F]'
                  : 'text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500'
              }`}
            />
          </motion.div>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pb-8 pl-[52px] pr-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {item.respuesta}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [promo, setPromo] = useState<PromocionActiva | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Promo activa: alimenta tanto la respuesta visible en el acordeón como
  // el JSON-LD de FaqSEO (para que buscadores no indexen una fecha caducada).
  useEffect(() => {
    cargarPromocionActiva().then(setPromo);
  }, []);

  const respuestaGratis = useMemo(
    () => formatearDescripcionPromocion(promo),
    [promo]
  );

  const faqData = useMemo(() => buildFaqData(respuestaGratis), [respuestaGratis]);

  const normalizar = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filteredData = faqData
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          (!search ||
            normalizar(item.pregunta).includes(normalizar(search)) ||
            item.keywords?.some((kw) =>
              normalizar(kw).includes(normalizar(search))
            )) &&
          (!activeCategory || cat.categoria === activeCategory)
      ),
    }))
    .filter((cat) => cat.items.length > 0);

  let globalCounter = 0;

  const totalPreguntas = faqData.reduce((acc, c) => acc + c.items.length, 0);

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-100">
      <FaqSEO respuestaGratis={respuestaGratis} />

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, funde con el resto de la app */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40,142,127,0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16,50,84,0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        <button
          onClick={() => navigate(-1)}
          aria-label="Volver atrás"
          className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-5 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <HelpCircle className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            Preguntas frecuentes
          </motion.h1>

          <motion.p
            className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            Todo lo que necesitas saber sobre Bookoll.{' '}
            <span className="text-white/50">{totalPreguntas} respuestas.</span>
          </motion.p>

          <motion.div
            className="mt-8 w-full max-w-lg relative"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input
              type="text"
              placeholder="Busca una pregunta..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-full pl-11 pr-5 py-5 text-sm text-gray-700 dark:text-white shadow-xl bg-white/95 dark:bg-[#1c1c1e]/95 focus:ring-2 focus:ring-[#103254] dark:focus:ring-white/30 border-0 dark:placeholder:text-gray-500"
            />
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── CATEGORY FILTER ─────────────────────────────────────────────────── */}
      <nav
        aria-label="Categorías de preguntas frecuentes"
        className="sticky top-0 z-20 bg-white/90 dark:bg-black/90 backdrop-blur-sm border-b border-gray-100 dark:border-white/10 shadow-sm"
      >
        <div className="max-w-3xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveCategory(null)}
            aria-pressed={activeCategory === null}
            className={`shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
              activeCategory === null
                ? 'bg-[#288E7F] text-white shadow-sm'
                : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20'
            }`}
          >
            Todas
          </button>
          {faqData.map((cat) => (
            <button
              key={cat.categoria}
              onClick={() =>
                setActiveCategory((p) =>
                  p === cat.categoria ? null : cat.categoria
                )
              }
              aria-pressed={activeCategory === cat.categoria}
              className={`shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                activeCategory === cat.categoria
                  ? 'bg-[#288E7F] text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20'
              }`}
            >
              {cat.icon}
              {cat.categoria}
            </button>
          ))}
        </div>
      </nav>

      {/* ── CONTENT ─────────────────────────────────────────────────────────── */}
      <section
        aria-label="Preguntas frecuentes sobre Bookoll"
        className="py-16 w-full flex-1 bg-white dark:bg-black"
      >
        <div className="max-w-3xl mx-auto px-6 space-y-0">
          {filteredData.length > 0 ? (
            filteredData.map((cat) => (
              <motion.div
                key={cat.categoria}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4 }}
                className="mb-10"
              >
                <div className="flex items-center gap-3 mb-2 pb-4 border-b border-[#288E7F]/20">
                  <div className="w-8 h-8 rounded-lg bg-[#288E7F]/10 flex items-center justify-center text-[#288E7F]">
                    {cat.icon}
                  </div>
                  <h2 className="text-lg font-semibold text-[#103254] dark:text-white">
                    {cat.categoria}
                  </h2>
                  <span className="ml-auto text-xs font-bold text-[#288E7F]/50">
                    {cat.items.length}{' '}
                    {cat.items.length === 1 ? 'pregunta' : 'preguntas'}
                  </span>
                </div>

                {cat.items.map((item, idx) => {
                  const gi = globalCounter++;
                  return (
                    <AccordionItem
                      key={idx}
                      item={item}
                      index={idx}
                      globalIndex={gi}
                    />
                  );
                })}
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-20 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                No se encontraron resultados para{' '}
                <strong className="text-gray-700 dark:text-gray-200">"{search}"</strong>
              </p>
              <button
                onClick={() => setSearch('')}
                className="mt-3 text-xs font-semibold text-[#288E7F] underline underline-offset-2"
              >
                Limpiar búsqueda
              </button>
            </motion.div>
          )}

          {filteredData.length > 0 && (
            <div className="pt-12 pb-4 text-center">
              <span className="inline-block text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-full px-4 py-1.5">
                ¿No encuentras lo que buscas?{' '}
                <a
                  href="mailto:soporte@bookoll.com"
                  className="text-[#103254] dark:text-white font-semibold hover:opacity-80"
                >
                  Escríbenos
                </a>
              </span>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;